"use client";

import React, { useActionState, useRef, useState, startTransition, useEffect } from "react";
import Image from "next/image";

import { Button } from "@/components/ui/button";

import { type ForgotPasswordState, forgotPasswordAction } from "@/actions/auth";
import CustomInput from "@/app/_components/CustomInput";
import Heading from "@/app/_components/Heading";

import ErrorBanner from "@/app/_components/ErrorBanner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { forgotPasswordSchema } from "@/schema/auth";
import type { z } from "zod";

interface OTPLoginProps {
  onSwitchToEmail: () => void;
  onNext: (phoneNumber: string) => void;
}

const OTPLogin = ({ onSwitchToEmail, onNext }: OTPLoginProps) => {
  const [state, formAction, isPending] = useActionState<ForgotPasswordState, FormData>(forgotPasswordAction, {
    success: false,
  });

  const [isErrorDismissed, setIsErrorDismissed] = useState(false);

  // react-hook-form for client validation
  type FormValues = z.infer<typeof forgotPasswordSchema>;
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const formRef = useRef<HTMLFormElement | null>(null);

  const dismissError = () => {
    setIsErrorDismissed(true);
  };

  // Advance to next step on successful response
  useEffect(() => {
    if (state.success && onNext) {
      const formData = new FormData(formRef.current as HTMLFormElement);
      const phoneNumber = formData.get('phone') as string;
      onNext(phoneNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.success]);

  return (
    <div className="flex max-w-120 flex-col">
      {state.message && !state.success && !isErrorDismissed && (
        <ErrorBanner message={state.message} onDismiss={dismissError} />
      )}
      
      <Heading size="3xl" className="text-text-900 mb-10.5">
        Admin Login
      </Heading>

      <form
        ref={formRef}
        className="mb-7 flex flex-col gap-4"
        noValidate
        onSubmit={handleSubmit(() => {
          setIsErrorDismissed(false);
          const fd = new FormData(formRef.current as HTMLFormElement);
          startTransition(() => {
            formAction(fd);
          });
        })}
      >
        <CustomInput
          label="Enter Phone number"
          type="tel"
          placeholder="Enter your phone number"
          error={errors.phone?.message ?? state.errors?.phone?.[0]}
          {...register("phone")}
          name="phone"
        />

    <Button type="submit" disabled={isPending || isSubmitting}>
          {"Get OTP"}
        </Button>
      </form>

      <div className="mb-7 flex w-full items-center">
        <div className="bg-outline-600 h-px flex-grow"></div>
        <span className="text-body-medium/sm text-text-950 mx-3">OR</span>
        <div className="bg-outline-600 h-px flex-grow"></div>
      </div>
      
      <Button variant={"tertiary"} onClick={onSwitchToEmail}>
        <span className="mr-3">Login with Email</span>{" "}
        <Image src="/images/login/mail.svg" alt="mail" height={18} width={18} />
      </Button>
    </div>
  );
};

export default OTPLogin;
